import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2, Send, Users, ShieldOff, Trash2, Pencil, X, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDate, REGIONS } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BrevoCampaigns } from "@/components/BrevoCampaigns";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600",
  scheduled: "bg-amber-100 text-amber-800",
  sending: "bg-blue-100 text-blue-800",
  sent: "bg-emerald-100 text-emerald-800",
  failed: "bg-red-100 text-red-800",
};
const SOURCES = [
  { key: "contacts", label: "Contacts" },
  { key: "leads", label: "Leads" },
  { key: "dealers", label: "Dealers" },
  { key: "universe", label: "Universe (target accounts)" },
  { key: "custom", label: "Custom list (saved lists)" },
  { key: "adhoc", label: "Quick send — type recipients in" },
];
const SOURCE_LABELS = { contacts: "Contacts", leads: "Leads", dealers: "Dealers", universe: "Universe", custom: "Custom list", adhoc: "Quick send" };
const UNI_CLASSES = ["A+", "A", "B+", "B", "C"];
const ZONES = REGIONS.filter((r) => r !== "National");

const EMAIL_RX = /^[^\s<>,;"]+@[^\s<>,;"]+\.[^\s<>,;"]+$/;

export function RecipientRows({ rows, onChange, testPrefix = "recip" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const add = () => {
    const em = email.trim().toLowerCase();
    if (!EMAIL_RX.test(em)) { toast.error("Enter a valid email address"); return; }
    if (rows.some((r) => r.email === em)) { toast.error("Already added"); return; }
    onChange([...rows, { name: name.trim(), email: em }]);
    setName(""); setEmail("");
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="h-9" data-testid={`${testPrefix}-name-input`} />
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@company.com" className="h-9"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid={`${testPrefix}-email-input`} />
        <Button type="button" onClick={add} className="h-9 bg-slate-900 hover:bg-slate-800 shrink-0 gap-1 px-3" data-testid={`${testPrefix}-add-btn`}><Plus className="h-4 w-4" /> Add</Button>
      </div>
      {rows.length > 0 && (
        <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 divide-y" data-testid={`${testPrefix}-rows`}>
          {rows.map((r, i) => (
            <div key={r.email} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm">
              <span className="truncate">{r.name && <span className="font-medium mr-1.5">{r.name}</span>}<span className="font-mono text-xs text-slate-500">{r.email}</span></span>
              <button type="button" onClick={() => onChange(rows.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-500 shrink-0" data-testid={`${testPrefix}-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400">{rows.length} recipient{rows.length === 1 ? "" : "s"} · duplicates and opted-out addresses are excluded automatically</p>
    </div>
  );
}

function parseBulk(text) {
  const found = [];
  for (const line of String(text || "").split(/\r?\n/)) {
    const m = line.match(/<?([^\s<>,;"]+@[^\s<>,;"]+\.[^\s<>,;"]+)>?/);
    if (!m) continue;
    const email = m[1].toLowerCase().replace(/\.+$/, "");
    if (!EMAIL_RX.test(email)) continue;
    const nm = (line.slice(0, m.index) + " " + line.slice(m.index + m[0].length)).replace(/[<>,;"]/g, " ").replace(/\s+/g, " ").trim();
    found.push({ email, name: nm });
  }
  return found;
}

export function ListEditorDialog({ open, onOpenChange, listId, onSaved }) {
  const [name, setName] = useState("");
  const [rows, setRows] = useState([]);
  const [raw, setRaw] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRaw(""); setShowPaste(false);
    if (listId) {
      setLoading(true);
      api.get(`/marketing/lists/${listId}`)
        .then(({ data }) => { setName(data.name || ""); setRows(data.recipients || []); })
        .catch((e) => toast.error(apiError(e)))
        .finally(() => setLoading(false));
    } else { setName(""); setRows([]); }
  }, [open, listId]);

  const appendRows = (found) => {
    setRows((prev) => {
      const seen = new Set(prev.map((r) => r.email));
      const add = found.filter((r) => !seen.has(r.email) && (seen.add(r.email), true));
      if (add.length) toast.success(`${add.length} recipient${add.length === 1 ? "" : "s"} added`);
      else toast.info("No new valid emails found");
      return [...prev, ...add];
    });
  };
  const readFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => appendRows(parseBulk(String(reader.result || "")));
    reader.readAsText(file);
  };
  const save = async () => {
    if (!name.trim()) { toast.error("Give the list a name"); return; }
    if (!rows.length) { toast.error("Add at least one recipient"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), recipients: rows };
      const { data } = listId
        ? await api.put(`/marketing/lists/${listId}`, body)
        : await api.post("/marketing/lists", body);
      toast.success(`List saved — ${data.count} recipient${data.count === 1 ? "" : "s"}`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="list-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{listId ? "Edit Audience List" : "New Audience List"}</DialogTitle>
          <DialogDescription>Add people one by one, or paste / upload many at once.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">List name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Expo Visitors — Mumbai 2026" data-testid="list-name-input" /></div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Recipients *</Label>
                <button type="button" onClick={() => setShowPaste(!showPaste)} className="text-[11px] font-medium text-[#f04935] hover:underline" data-testid="list-toggle-paste">
                  {showPaste ? "Hide bulk paste" : "Paste many / upload CSV"}
                </button>
              </div>
              <RecipientRows rows={rows} onChange={setRows} testPrefix="list-recip" />
            </div>
            {showPaste && (
              <div className="space-y-1.5 rounded-md border border-dashed border-slate-200 p-2.5 bg-slate-50/60">
                <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={4}
                  placeholder={"rahul@acme.in\nPriya Shah, priya@studio.co\nAmit Verma <amit@builders.com>"}
                  className="w-full rounded-md border border-slate-200 p-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-300"
                  data-testid="list-paste-textarea" />
                <div className="flex items-center justify-between">
                  <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => { appendRows(parseBulk(raw)); setRaw(""); }} data-testid="list-paste-add-btn">Add pasted emails</Button>
                  <label className="text-[11px] font-medium text-[#f04935] hover:underline cursor-pointer" data-testid="list-file-label">
                    Upload CSV / TXT<input type="file" accept=".csv,.txt" className="hidden" onChange={readFile} data-testid="list-file-input" />
                  </label>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading || !rows.length} className="bg-slate-900 hover:bg-slate-800" data-testid="list-save-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{listId ? "Save Changes" : "Save List"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaveAsListInline({ rows }) {
  const qc = useQueryClient();
  const [openInput, setOpenInput] = useState(false);
  const [name, setName] = useState("");
  const save = async () => {
    if (!name.trim()) { toast.error("Give the list a name"); return; }
    try {
      const { data } = await api.post("/marketing/lists", { name: name.trim(), recipients: rows });
      toast.success(`Saved as list "${data.name}" — reusable in future campaigns`);
      setOpenInput(false); setName("");
      qc.invalidateQueries({ queryKey: ["marketing-lists"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return openInput ? (
    <div className="flex gap-1.5">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="List name" className="h-8 text-sm" data-testid="save-as-list-name" />
      <Button type="button" size="sm" className="h-8 bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="save-as-list-confirm">Save</Button>
      <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setOpenInput(false)}>Cancel</Button>
    </div>
  ) : (
    <button type="button" onClick={() => setOpenInput(true)} className="text-[11px] font-medium text-[#f04935] hover:underline" data-testid="save-as-list-btn">Save these as a reusable list</button>
  );
}

export function AudiencePicker({ source, setSource, filters, setFilters }) {
  const qc = useQueryClient();
  const [listOpen, setListOpen] = useState(false);
  const [editListId, setEditListId] = useState(null);
  const setF = (k, v) => setFilters({ ...filters, [k]: v });
  const toggleCat = (c) => {
    const cur = filters.categories || [];
    setF("categories", cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);
  };
  const { data: lists = [] } = useQuery({
    queryKey: ["marketing-lists"],
    queryFn: async () => (await api.get("/marketing/lists")).data,
    enabled: source === "custom",
  });
  const delList = async (id) => {
    try {
      await api.delete(`/marketing/lists/${id}`);
      toast.success("List deleted");
      if (filters.list_id === id) setFilters({});
      qc.invalidateQueries({ queryKey: ["marketing-lists"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Audience *</Label>
        <Select value={source} onValueChange={(v) => { setSource(v); setFilters({}); }}>
          <SelectTrigger className="h-9" data-testid="audience-source-select"><SelectValue placeholder="Pick an audience" /></SelectTrigger>
          <SelectContent>{SOURCES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {(source === "leads" || source === "dealers") && (
        <div className="space-y-1.5">
          <Label className="text-xs">Zone</Label>
          <Select value={filters.zone || "all"} onValueChange={(v) => setF("zone", v === "all" ? "" : v)}>
            <SelectTrigger className="h-9" data-testid="audience-zone-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All zones</SelectItem>{ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {source === "leads" && (
        <div className="space-y-1.5"><Label className="text-xs">Branch / City contains</Label>
          <Input value={filters.branch || ""} onChange={(e) => setF("branch", e.target.value)} className="h-9" placeholder="e.g. Mumbai" data-testid="audience-branch-input" /></div>
      )}
      {source === "universe" && (
        <div className="space-y-1.5">
          <Label className="text-xs">Class</Label>
          <div className="flex gap-1.5 flex-wrap">
            {UNI_CLASSES.map((c) => (
              <button key={c} type="button" onClick={() => toggleCat(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${(filters.categories || []).includes(c) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                data-testid={`audience-class-${c.replace("+", "plus")}`}>{c}</button>
            ))}
          </div>
        </div>
      )}
      {source === "custom" && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Custom list</Label>
            <button type="button" onClick={() => setListOpen(true)} className="text-[11px] font-medium text-[#f04935] hover:underline" data-testid="new-list-btn">+ New list</button>
          </div>
          <div className="flex items-center gap-1.5">
            <Select value={filters.list_id || ""} onValueChange={(v) => {
              const l = lists.find((x) => x.id === v);
              setFilters({ ...filters, list_id: v, list_name: l?.name || "" });
            }}>
              <SelectTrigger className="h-9 flex-1" data-testid="audience-list-select"><SelectValue placeholder={lists.length ? "Pick a saved list…" : "No lists yet — create one"} /></SelectTrigger>
              <SelectContent className="max-h-56">
                {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} · {l.count} emails</SelectItem>)}
              </SelectContent>
            </Select>
            {filters.list_id && (
              <>
                <Button type="button" size="sm" variant="ghost" className="h-9 px-2 text-slate-500 hover:text-slate-800" onClick={() => { setEditListId(filters.list_id); setListOpen(true); }} title="Edit this list" data-testid="edit-list-btn"><Pencil className="h-4 w-4" /></Button>
                <Button type="button" size="sm" variant="ghost" className="h-9 px-2 text-red-500 hover:text-red-700" onClick={() => delList(filters.list_id)} title="Delete this list" data-testid="delete-list-btn"><Trash2 className="h-4 w-4" /></Button>
              </>
            )}
          </div>
          <ListEditorDialog open={listOpen} onOpenChange={(v) => { setListOpen(v); if (!v) setEditListId(null); }} listId={editListId}
            onSaved={(l) => { qc.invalidateQueries({ queryKey: ["marketing-lists"] }); setFilters({ ...filters, list_id: l.id, list_name: l.name }); }} />
        </div>
      )}
      {source === "adhoc" && (
        <div className="space-y-2">
          <Label className="text-xs">Type in recipients — no list needed</Label>
          <RecipientRows rows={filters.recipients || []} onChange={(rows) => setFilters({ ...filters, recipients: rows })} testPrefix="adhoc" />
          {(filters.recipients || []).length > 0 && <SaveAsListInline rows={filters.recipients} />}
        </div>
      )}
    </div>
  );
}

function NewCampaignDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ name: "", subject: "", template_id: "", sender_email: "", sender_name: "" });
  const [source, setSource] = useState("");
  const [filters, setFilters] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: templates = [] } = useQuery({
    queryKey: ["email-templates", ""],
    queryFn: async () => (await api.get("/marketing/templates")).data,
    enabled: open,
  });
  const { data: senders } = useQuery({
    queryKey: ["brevo-senders"],
    queryFn: async () => (await api.get("/marketing/senders")).data,
    enabled: open,
    retry: false,
  });
  const { data: audience, isFetching: counting } = useQuery({
    queryKey: ["audience-preview", source, JSON.stringify(filters)],
    queryFn: async () => (await api.post("/marketing/audience/preview", { source, filters })).data,
    enabled: open && !!source,
  });

  const create = async () => {
    if (!f.name.trim() || !f.subject.trim() || !f.template_id) { toast.error("Name, subject and template are required"); return; }
    if (!source) { toast.error("Pick an audience"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/marketing/campaigns", {
        ...f, audience_source: source, audience_filters: filters,
        sender_email: f.sender_email || undefined, sender_name: f.sender_name || undefined,
      });
      onOpenChange(false);
      navigate(`/marketing/campaigns/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setStep(1); setF({ name: "", subject: "", template_id: "", sender_email: "", sender_name: "" }); setSource(""); setFilters({}); } }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New Campaign — step {step} of 2</DialogTitle>
          <DialogDescription>{step === 1 ? "Pick the template and set the email basics." : "Choose who receives this email."}</DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Campaign name *</Label>
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. July Dealer Newsletter" data-testid="campaign-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Email subject *</Label>
              <Input value={f.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Type your subject line…" data-testid="campaign-subject-input" />
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-slate-400">Merge tags personalise it per recipient.</p>
                <button type="button" onClick={() => set("subject", `${f.subject || ""}{{ params.first_name }}`)} className="text-[11px] font-medium text-[#f04935] hover:underline" data-testid="subject-insert-first-name">+ insert first-name tag</button>
              </div></div>
            <div className="space-y-1.5"><Label className="text-xs">Template *</Label>
              <Select value={f.template_id} onValueChange={(v) => set("template_id", v)}>
                <SelectTrigger className="h-9" data-testid="campaign-template-select"><SelectValue placeholder="Pick a saved template" /></SelectTrigger>
                <SelectContent className="max-h-56">{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Sender</Label>
              <Select value={f.sender_email || "__default"} onValueChange={(v) => set("sender_email", v === "__default" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="campaign-sender-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default">Default ({senders?.default?.email || "configured sender"})</SelectItem>
                  {(senders?.senders || []).filter((s) => s.active).map((s) => <SelectItem key={s.email} value={s.email}>{s.name} — {s.email}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">Senders must be verified in Brevo for good deliverability.</p></div>
          </div>
        ) : (
          <div className="space-y-3">
            <AudiencePicker source={source} setSource={setSource} filters={filters} setFilters={setFilters} />
            {source && (
              <Card className="p-3 bg-slate-50 border-slate-200" data-testid="audience-count-card">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-500" />
                  {counting ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
                    <p className="text-sm"><span className="font-bold font-mono">{audience?.count ?? 0}</span> reachable recipients <span className="text-slate-400 text-xs">(valid email, deduped, opt-outs excluded)</span></p>
                  )}
                </div>
                {(audience?.sample || []).length > 0 && (
                  <p className="text-[11px] text-slate-400 mt-1.5 truncate">e.g. {(audience.sample || []).slice(0, 3).map((s) => s.email).join(", ")}…</p>
                )}
              </Card>
            )}
          </div>
        )}
        <DialogFooter>
          {step === 2 && <Button variant="outline" onClick={() => setStep(1)}>Back</Button>}
          {step === 1 ? (
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => {
              if (!f.name.trim() || !f.subject.trim() || !f.template_id) { toast.error("Name, subject and template are required"); return; }
              setStep(2);
            }} data-testid="campaign-next-btn">Next — Audience</Button>
          ) : (
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={create} disabled={saving || !audience?.count} data-testid="campaign-create-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Create Draft & Review</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AudienceListsCard() {
  const qc = useQueryClient();
  const [editor, setEditor] = useState(null);
  const { data: lists = [] } = useQuery({
    queryKey: ["marketing-lists"],
    queryFn: async () => (await api.get("/marketing/lists")).data,
  });
  const del = async (l) => {
    try {
      await api.delete(`/marketing/lists/${l.id}`);
      toast.success(`List "${l.name}" deleted`);
      qc.invalidateQueries({ queryKey: ["marketing-lists"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-5 mt-6" data-testid="audience-lists-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-slate-500" />
          <h3 className="font-heading font-semibold text-sm">Audience Lists — {lists.length} saved</h3>
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setEditor({})} data-testid="lists-new-btn"><Plus className="h-3.5 w-3.5" /> New List</Button>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Reusable recipient lists for campaigns — add people one by one or paste/upload in bulk.</p>
      {lists.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">No lists yet — create one, or use "Quick send" in a campaign to type recipients directly.</p>
      ) : (
        <div className="divide-y" data-testid="audience-lists-rows">
          {lists.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-2 py-2" data-testid={`list-row-${l.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{l.name}</p>
                <p className="text-[11px] text-slate-400">{l.count} recipient{l.count === 1 ? "" : "s"} · {l.created_by_name || ""} · {formatDate(l.updated_at || l.created_at)}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-500 hover:text-slate-800" onClick={() => setEditor({ id: l.id })} title="Edit list" data-testid={`list-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="h-8 px-2 text-red-500 hover:text-red-700" onClick={() => del(l)} title="Delete list" data-testid={`list-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ListEditorDialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)} listId={editor?.id}
        onSaved={() => qc.invalidateQueries({ queryKey: ["marketing-lists"] })} />
    </Card>
  );
}

function SuppressionCard() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [expanded, setExpanded] = useState(false);
  const { data: optouts = [] } = useQuery({
    queryKey: ["marketing-optouts"],
    queryFn: async () => (await api.get("/marketing/optouts")).data,
  });
  const add = async () => {
    if (!email.trim()) return;
    try {
      await api.post("/marketing/optout", { email: email.trim(), opt_out: true });
      toast.success("Added to suppression list"); setEmail("");
      qc.invalidateQueries({ queryKey: ["marketing-optouts"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (em) => {
    try {
      await api.post("/marketing/optout", { email: em, opt_out: false });
      toast.success("Opted back in");
      qc.invalidateQueries({ queryKey: ["marketing-optouts"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-5 mt-6" data-testid="suppression-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldOff className="h-4 w-4 text-slate-500" />
          <h3 className="font-heading font-semibold text-sm">Suppression list — {optouts.length} opted out</h3>
        </div>
        <div className="flex items-center gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Add email to suppress" className="h-8 w-[220px] text-sm" data-testid="optout-email-input" />
          <Button size="sm" variant="outline" className="h-8" onClick={add} data-testid="optout-add-btn">Suppress</Button>
          {optouts.length > 0 && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setExpanded(!expanded)} data-testid="optout-toggle-btn">{expanded ? "Hide" : "Show list"}</Button>}
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-1.5">Opted-out addresses are never included in campaign audiences. Transactional emails (quotes, orders) are unaffected.</p>
      {expanded && (
        <div className="mt-3 max-h-56 overflow-y-auto divide-y">
          {optouts.map((o) => (
            <div key={o.email} className="flex items-center justify-between py-1.5 text-sm">
              <span className="font-mono text-xs">{o.email} <span className="text-slate-400">· {o.reason ? `${o.reason} · ` : ""}{o.source}{o.at ? ` · ${formatDate(o.at)}` : ""}</span></span>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => remove(o.email)}>Opt back in</Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Campaigns() {
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  const [tab, setTab] = useState("crm");
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => (await api.get("/marketing/campaigns")).data,
    refetchInterval: 15000,
  });

  return (
    <div>
      <PageHeader title="Campaigns" subtitle={tab === "crm" ? "Pick a template, choose the audience, test, then send or schedule." : "Brevo-native campaigns — performance pulled straight from Brevo."}>
        {tab === "crm" && <Button onClick={() => setNewOpen(true)} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="new-campaign-btn"><Plus className="h-4 w-4" /> New Campaign</Button>}
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="crm" data-testid="campaigns-tab-crm">CRM Campaigns</TabsTrigger>
          <TabsTrigger value="brevo" data-testid="campaigns-tab-brevo">Brevo Campaigns</TabsTrigger>
        </TabsList>

        <TabsContent value="brevo"><BrevoCampaigns /></TabsContent>
        <TabsContent value="crm">
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Campaign", "Template", "Audience", "Recipients", "Status", "Opened", "Clicked", "Date"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/marketing/campaigns/${c.id}`)} data-testid={`campaign-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-slate-600">{c.template_name}</TableCell>
                    <TableCell className="text-slate-600">{SOURCE_LABELS[c.audience_source] || c.audience_source}</TableCell>
                    <TableCell className="font-mono">{c.recipient_count}</TableCell>
                    <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLE[c.status] || ""}`}>{c.status}</span></TableCell>
                    <TableCell className="font-mono text-sm">{c.stats ? c.stats.opened : "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{c.stats ? c.stats.clicked : "—"}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{formatDate(c.sent_at || c.scheduled_at || c.created_at)}</TableCell>
                  </TableRow>
                ))}
                {campaigns.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400"><Send className="h-6 w-6 mx-auto mb-2 text-slate-300" />No campaigns yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      <AudienceListsCard />
      <SuppressionCard />
        </TabsContent>
      </Tabs>
      <NewCampaignDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
